import { MouseEvent, useCallback, useEffect, useMemo, useRef } from "react";
import { Portal } from "@mui/core";
import { Box, Fab, SvgIcon } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useHistory, useLocation, useParams } from "react-router-dom";
import * as L from "leaflet";
import "leaflet-lasso";

import { HistoryState } from "src/types";
import { useSegments } from "src/hooks/segments/useSegments";
import { SegmentsOnMap } from "src/components/segments/SegmentsOnMap";
import { ReactComponent as LassoIcon } from "src/assets/icons/lasso.svg";
import { useSegmentDraw } from "src/hooks/useSegmentDraw";
import { queryClient } from "../../query-client";
import { segmentsSelectionMachine } from "src/machines/segments-selection.machine";
import { useMachine } from "@xstate/react";
import { compact, xor } from "lodash-es";

// TODO: type this elsewhere
interface LassoHandlerFinishedEventData {
  originalEvent: MouseEvent;
  latLngs: L.LatLng[];
  layers: L.Layer[];
}
type LassoHandlerFinishedEvent = L.LeafletEvent & LassoHandlerFinishedEventData;

interface Props {
  map: L.Map;
  controlContainer: Element | null;
}

export function TrafficEmissionPage(props: Props) {
  const location = useLocation<HistoryState>();
  const history = useHistory<HistoryState>();
  const params = useParams<{
    caseId: string;
    scenarioId: string;
    emissionId: string;
    segmentId?: string;
  }>();

  const [state, send] = useMachine(segmentsSelectionMachine, {
    actions: {
      createNewSegment: (_content, event) => {
        history.push(`${trafficEmissionPath}/segments/new`, { drawing: event.drawing });
      },
      selectNew: (context, event) => {
        const selection = event.payload;
        routeToSegmentEdit(selection);

        // return { ...context, selection: event.payload };
      },
      selectAdd: (context, event) => {
        const currentSelection = currentSegmentSelection(location.state, params);
        const selection = [...currentSelection, ...event.payload];
        routeToSegmentEdit(selection);
      },
      selectAddXor: (context, event) => {
        const currentSelection = currentSegmentSelection(location.state, params);
        const selection = xor(currentSelection, event.payload);
        routeToSegmentEdit(selection);
      },
    },
  });
  const { data: segments } = useSegments(params.scenarioId);

  // TODO: refactor lasso init, effects and other into a custom hook?
  const lasso = useRef(L.lasso(props.map, { intersect: true }));

  const { startDrawing, stopDrawing } = useSegmentDraw(props.map, {
    onDraw: (drawing) => send({ type: "CREATE_SEGMENT", drawing }),
  });

  const trafficEmissionPath = useMemo(
    () => `/cases/${params.caseId}/scenarios/${params.scenarioId}/emissions/traffic/${params.emissionId}`,
    [params.caseId, params.emissionId, params.scenarioId]
  );

  const routeToSegmentEdit = useCallback(
    (selection: number[]) => {
      if (selection.length === 1) {
        // route to single edit form
        history.push(`${trafficEmissionPath}/segments/${selection[0]}`);
      } else if (selection.length > 1) {
        // route to bulk edit form
        history.push(`${trafficEmissionPath}/segments/bulk`, { bulkEdit: selection });
      }
    },
    [history, trafficEmissionPath]
  );

  // TODO: refactor this effect into a seperate hook/function? Think about it!
  useEffect(() => {
    if (state.matches("draw")) {
      // do nothing
      // return empty cleanup
      return () => {};
    } else if (!state.matches("select_with_lasso")) {
      props.map.once("click", (event: L.LeafletMouseEvent) => {
        history.push(trafficEmissionPath);
        send({ type: "CANCEL" });
      });
      // clenaup once event handler
      return function cleanUp() {
        props.map.off("click");
      };
    }
  }, [history, props.map, send, state, trafficEmissionPath]);

  useEffect(() => {
    // TODO: rerenderWMS is outdated, segments are now rendered via geojson, thus update naming
    if (location.state?.action === "rerenderWMS") {
      queryClient.invalidateQueries("segments");
    }
  }, [history, location]);

  useEffect(() => {
    if (!state.matches("select_with_lasso")) lasso.current.disable();
  }, [lasso, state]);

  useEffect(() => {
    if (!state.matches("draw")) stopDrawing();
  }, [lasso, state, stopDrawing]);

  function handleDrawToggle() {
    // if already drawing, reset to 'select'
    if (state.matches("draw")) return send({ type: "CANCEL" });
    // else start drawing
    else {
      send({ type: "DRAW_SEGMENT" });
      queueMicrotask(startDrawing);
    }
  }

  // TODO: refactor lasso init, effects and other into a custom hook?
  function handleLassoToggle() {
    // if already lasso_selecting, reset to 'select'
    if (state.matches("select_with_lasso")) {
      send({ type: "CANCEL" });
      return;
    }

    // else continue enabling lasso
    if (!props.map) return;

    // move this into context state?
    send({ type: "LASSO_SELECT" });
    lasso.current.enable();

    // @ts-ignore
    props.map.on("lasso.finished", (event: LassoHandlerFinishedEvent, ...rest) => {
      // @ts-ignore
      L.DomEvent.stopPropagation(event.originalEvent);

      // construct segments list
      // all 'shapes' are selected, therefore filter only the segments
      const lassoIds = event.layers.reduce((selectionAcc: number[], layer) => {
        // @ts-ignore
        const featureId = layer.feature.properties.id;

        if (segments?.some((segment) => segment.segmentId === featureId)) return [...selectionAcc, featureId];
        else return selectionAcc;
      }, []);

      setTimeout(() => {
        if (event.originalEvent.shiftKey) {
          send({ type: "SELECT_ADD", payload: lassoIds });
        } else {
          send({ type: "SELECT_NEW", payload: lassoIds });
        }
      }, 1);

      props.map.off("lasso.finished");
    });
  }

  return render();

  function render() {
    if (!segments) return null;

    return (
      <>
        <SegmentsOnMap
          map={props.map}
          segments={segments}
          selected={currentSegmentSelection(location.state, params)}
          onSelect={(segment, event) => {
            const addToSelection = event.originalEvent.shiftKey;

            if (addToSelection) {
              send({ type: "SELECT_ADD_XOR", payload: [segment.segmentId] });
            } else {
              send({ type: "SELECT_NEW", payload: [segment.segmentId] });
            }
          }}
        />

        <Portal container={props.controlContainer}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Fab
              size="small"
              onClick={(event) => handleDrawToggle()}
              color={state.matches("draw") ? "primary" : "default"}
            >
              <AddIcon fontSize="small" />
            </Fab>
            <Fab
              size="small"
              onClick={(event) => handleLassoToggle()}
              color={state.matches("select_with_lasso") ? "primary" : "default"}
            >
              <SvgIcon component={LassoIcon} viewBox="0 0 100 100" />
            </Fab>
          </Box>
        </Portal>
      </>
    );
  }
}

function currentSegmentSelection(
  locationState: HistoryState,
  params: Partial<{ segmentId: string | undefined }>
): number[] {
  const selectionInLocationState = (locationState?.bulkEdit || []).map((id) => +id);
  const selectionInParams = params.segmentId && params.segmentId !== "bulk" ? +params.segmentId : null;
  return compact([...selectionInLocationState, selectionInParams]);
}
